/**
 * CSV アップロード Step1 の共通読み取り関数 (`admin/data/uploads/common.md`
 * AC-DUC-043 / AC-DUC-045)。
 *
 * RFC 4180 の囲み (`"..."` 内の区切り・改行・`""`) に従ってテキスト全体を
 * レコード × セルへ分割し、囲みが正しくない行を検出する。判定は backend の
 * `encoding/csv` (既定設定) と揃える — 前段だけが緩い / 厳しいと、画面は通ったのに
 * 取込で落ちる、またはサーバーなら通る CSV を画面が弾く非対称が生まれる。
 *
 * - 囲みはセルの先頭でだけ開始できる。セルの途中や、囲みの前に空白がある `"` は
 *   「囲みの外の `"`」として検出する
 * - 閉じた `"` の直後は区切り・改行・終端のどれかでなければならない
 * - 閉じられないまま終端に達した囲みも検出する
 * - 囲み内の CRLF は LF へ正規化する (Go の `csv.Reader` と同じ)
 *
 * 区切り文字は固定 (`,` / `\t`)、または `auto` でレコードごとに判定できる。
 * 値の trim や空行の除外は呼び出し側が行う (空行は `['']` のレコードで返る。
 * 判定は `isBlankCsvRecord`)。
 */

export type CsvDelimiter = ',' | '\t'

export type CsvRecord = {
  cells: string[]
  /** このレコードが始まるファイル上の物理行番号 (1 起算。ヘッダー行 = 1)。 */
  line: number
}

/**
 * 読み取り結果。囲みが正しくない時は途中までのレコードを返さず、最初に囲みが
 * 正しくないレコードの開始行 (物理行番号) だけを返す。
 */
export type CsvReadResult =
  | { ok: true; records: CsvRecord[] }
  | { ok: false; quoteErrorRow: number }

/**
 * 空行 (= セルが 1 つだけで中身が空白) かどうか。`,,,` のように区切りだけがあり
 * 列数がヘッダーと合わない行は空行ではない (backend の `encoding/csv` と同じ)。
 */
export function isBlankCsvRecord(cells: string[]): boolean {
  return cells.length === 1 && cells[0].trim() === ''
}

/**
 * ヘッダー行より下に、全列が空値ではないデータ行が 1 行以上あるか。
 * 全列が空値の行 (`,,,` や空白だけのセルの行) は存在しないものとして数えない
 * (common.md AC-DUC-036)。ヘッダーのみ・全行が空のファイルを Step1 で止める判定
 * (AC-DUC-062) に使う。
 */
export function hasCsvDataRow(
  records: CsvRecord[],
  headerRowIndex: number,
): boolean {
  return records
    .slice(headerRowIndex + 1)
    .some((r) => r.cells.some((cell) => cell.trim() !== ''))
}

type State = 'fieldStart' | 'unquoted' | 'quoted' | 'afterQuote'

type RecordRead =
  | { ok: true; cells: string[]; next: number; newlines: number }
  | { ok: false }

const isNewlineAt = (text: string, i: number): boolean =>
  text[i] === '\n' || (text[i] === '\r' && text[i + 1] === '\n')

/** `start` から始まる 1 レコードを `delimiter` で読む (`start` < `text.length`)。 */
function readRecord(
  text: string,
  start: number,
  delimiter: CsvDelimiter,
): RecordRead {
  const cells: string[] = []
  let cur = ''
  let state: State = 'fieldStart'
  let newlines = 0
  for (let i = start; i < text.length; i++) {
    const ch = text[i]
    const isNewline = isNewlineAt(text, i)

    if (state === 'quoted') {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          state = 'afterQuote'
        }
      } else if (isNewline) {
        if (ch === '\r') i++
        cur += '\n'
        newlines++
      } else {
        cur += ch
      }
      continue
    }

    if (state === 'afterQuote' && !(ch === delimiter || isNewline)) {
      return { ok: false }
    }
    if (state === 'unquoted' && ch === '"') {
      return { ok: false }
    }

    if (isNewline) {
      cells.push(cur)
      return {
        ok: true,
        cells,
        next: i + (ch === '\r' ? 2 : 1),
        newlines: newlines + 1,
      }
    }
    if (ch === delimiter) {
      cells.push(cur)
      cur = ''
      state = 'fieldStart'
    } else if (state === 'fieldStart' && ch === '"') {
      state = 'quoted'
    } else {
      cur += ch
      state = 'unquoted'
    }
  }
  if (state === 'quoted') return { ok: false }
  cells.push(cur)
  return { ok: true, cells, next: text.length, newlines }
}

/**
 * `start` から始まるレコードの区切り文字を判定する。レコードの範囲は囲みの外の
 * 改行までで (区切り文字に依存しない)、その中に囲みの外のタブがあればタブ、
 * 無ければカンマ (スプレッドシートのコピペがタブ区切りになる慣習に合わせた
 * タブ優先。囲みの中のタブはセルの中身で区切りにしない)。
 */
function detectRecordDelimiter(text: string, start: number): CsvDelimiter {
  let inQuotes = false
  for (let i = start; i < text.length; i++) {
    const ch = text[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (!inQuotes && isNewlineAt(text, i)) {
      break
    } else if (!inQuotes && ch === '\t') {
      return '\t'
    }
  }
  return ','
}

/**
 * `delimiter` が `auto` の時は、レコードごとに区切り文字を判定する
 * (`supply-48-data.md` の区切り文字: 1 行内にタブがあればタブ、無ければカンマ)。
 */
export function readCsvRecords(
  rawText: string,
  delimiter: CsvDelimiter | 'auto',
): CsvReadResult {
  // 終端の単独 CR は読み飛ばす (Go の csv.Reader と同じ)。
  const text = rawText.endsWith('\r') ? rawText.slice(0, -1) : rawText
  const records: CsvRecord[] = []
  let pos = 0
  let line = 1
  while (pos < text.length) {
    const d =
      delimiter === 'auto' ? detectRecordDelimiter(text, pos) : delimiter
    const read = readRecord(text, pos, d)
    if (!read.ok) return { ok: false, quoteErrorRow: line }
    records.push({ cells: read.cells, line })
    line += read.newlines
    pos = read.next
  }
  return { ok: true, records }
}

/** 先頭 BOM (U+FEFF) が付いていれば取り除く。 */
export function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

export type DecodedFile =
  | { kind: 'ok'; text: string; bytes: Uint8Array }
  | { kind: 'encoding' }
  | { kind: 'read' }

/**
 * ファイル全体を、common.md §1「文字コードの判定」の順で復号する。
 * ① UTF-8 (BOM 付き / なし。BOM は除く) ② Shift_JIS (CP932) ③ どれにも当たらなければ
 * `encoding` (Step1 で拒否)。サーバー側 (Go の csvinput.DecodeUploaded) も同じ順で判定する。
 * 読み込み自体の失敗 (I/O エラー) は文字コードの問題ではないので `read` として区別する。
 *
 * `bytes` は元のファイルのバイト列。サーバーも同じ順で判定するので、送信はこれをそのまま使う
 * (UTF-8 に直すと Shift_JIS より大きくなり、送信の上限を超えうる)。
 */
export async function decodeCsvFile(file: File): Promise<DecodedFile> {
  let bytes: Uint8Array
  try {
    bytes = new Uint8Array(await file.arrayBuffer())
  } catch {
    return { kind: 'read' }
  }
  try {
    return {
      kind: 'ok',
      text: new TextDecoder('utf-8', { fatal: true }).decode(bytes),
      bytes,
    }
  } catch {
    // UTF-8 として読めない時は次の候補へ進む
  }
  try {
    return {
      kind: 'ok',
      text: stripBom(
        new TextDecoder('shift_jis', { fatal: true }).decode(bytes),
      ),
      bytes,
    }
  } catch {
    return { kind: 'encoding' }
  }
}
